import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import SiteModal from './SiteModal';
import SiteContactModal from './SiteContactModal';
import { siteService } from '../services/siteService';
import { siteContactService } from '../services/siteContactService';

const ViewSite = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactDeleteConfirm, setContactDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchSite = useCallback(async () => {
    try {
      setLoading(true);
      const res = await siteService.getSite(id);
      setSite(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load site.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSite();
  }, [fetchSite]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEditSite = async (formData) => {
    await siteService.updateSite(id, formData);
    await fetchSite();
    setShowEditSiteModal(false);
    showSuccess('Site updated successfully!');
  };

  const handleAddContact = async (formData) => {
    await siteContactService.createSiteContact(formData);
    await fetchSite();
    setShowContactModal(false);
    showSuccess('Contact added successfully!');
  };

  const handleEditContact = async (formData) => {
    await siteContactService.updateSiteContact(editingContact.id, formData);
    await fetchSite();
    setEditingContact(null);
    setShowContactModal(false);
    showSuccess('Contact updated successfully!');
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await siteContactService.deleteSiteContact(contactId);
      await fetchSite();
      setContactDeleteConfirm(null);
      showSuccess('Contact deleted successfully!');
    } catch (err) {
      setError('Failed to delete contact.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusBadge = (status) => {
    const styles = {
      'Quoted':      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      'Completed':   'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      'Cancelled':   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const cls = styles[status] || styles['Quoted'];
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || 'Quoted'}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-500">{error || 'Site not found.'}</div>
      </div>
    );
  }

  const contacts = site.site_contacts || [];
  const jobs = site.jobs || [];
  const addressParts = [site.street_address, site.town, site.county, site.postcode, site.country].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <Link to="/sites" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Sites</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">{site?.name || '…'}</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Site header card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{site.name}</h1>
              {site.customer && (
                <h2 className="mt-1 text-xl font-semibold">
                  <Link to={`/customers/${site.customer_id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{site.customer.name}</Link>
                </h2>
              )}
              {addressParts.length > 0 && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {[site.street_address, site.town, site.county].filter(Boolean).map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                  {site.country && <span className="block">{site.country}</span>}
                  {site.postcode && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:underline text-blue-500 dark:text-blue-400"
                    >
                      {site.postcode}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="shrink-0 flex flex-wrap gap-2">
              {addressParts.length > 0 && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                >
                  Open in Google Maps
                </a>
              )}
              <button
                onClick={() => setShowEditSiteModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                Edit Site
              </button>
            </div>
          </div>
        </div>

        {/* Site Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Site Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">
                {site.customer
                  ? <Link to={`/customers/${site.customer_id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{site.customer.name}</Link>
                  : '—'
                }
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Street Address</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{site.street_address || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Town</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{site.town || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">County</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{site.county || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{site.country || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Postcode</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">
                {site.postcode
                  ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([site.name, site.street_address, site.town, site.county, site.postcode].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{site.postcode}</a>
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Added</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{formatDate(site.created_at)}</dd>
            </div>
          </dl>
        </div>

        {/* Contacts */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Contacts ({contacts.length})
            </h2>
            <button
              onClick={() => { setEditingContact(null); setShowContactModal(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <span className="text-base leading-none">+</span> Add Contact
            </button>
          </div>
          {contacts.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No contacts at this site yet.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {contacts.map((contact) => (
                <div key={contact.id} className="py-4 flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {contact.first_name} {contact.last_name || ''}
                    </p>
                    {contact.position && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{contact.position}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 mt-1">
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {contact.email}
                        </a>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
                        >
                          {contact.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => { setEditingContact(contact); setShowContactModal(true); }}
                      className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setContactDeleteConfirm(contact)}
                      className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Jobs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Jobs ({jobs.length})
            </h2>
            <button
              onClick={() => navigate('/jobs')}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all jobs →
            </button>
          </div>
          {jobs.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No jobs at this site.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map((job) => (
                <div key={job.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <button
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                    >
                      {job.title}
                    </button>
                    {job.customer_po && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">PO: {job.customer_po}</p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(job.created_at)}</p>
                  </div>
                  <div className="shrink-0">{statusBadge(job.status)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Site Modal */}
      {showEditSiteModal && (
        <SiteModal
          site={site}
          onSave={handleEditSite}
          onClose={() => setShowEditSiteModal(false)}
        />
      )}

      {/* Add / Edit Contact Modal */}
      {showContactModal && (
        <SiteContactModal
          contact={editingContact}
          defaultSiteId={!editingContact ? id : undefined}
          onSave={editingContact ? handleEditContact : handleAddContact}
          onClose={() => { setShowContactModal(false); setEditingContact(null); }}
        />
      )}

      {/* Delete Contact Confirm */}
      {contactDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Contact</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete{' '}
              <strong>{contactDeleteConfirm.first_name} {contactDeleteConfirm.last_name}</strong>?
              This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setContactDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteContact(contactDeleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSite;
